// src/web/lib/hooks/useTrash.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, handleApiError } from '../api'
import { queryKeys } from '../query'

export interface TrashFolder {
	id: string
	name: string
	pathNames: string | null
	// Eden treaty revives date-like strings into Date objects at runtime
	deletedAt: string | Date | null
	deletedBy: string | null
	expiresAt: string | Date | null
}

export interface TrashAsset {
	id: string
	filename: string | null
	thumbnailUrl: string | null
	mediaTypeType: string | null
	dateBinned: string | Date | null
	expiresAt: string | Date | null
}

export interface TrashResponse {
	folders: TrashFolder[]
	assets: TrashAsset[]
}

export interface OrphanWarning {
	kind: 'folder' | 'asset'
	message: string
}

export interface TrashFolderContents extends TrashResponse {
	folder: { id: string; name: string }
	// Live (non-binned) assets still filed in this folder — purge would un-file them
	liveAssetCount: number
}

// Thrown when the browsed folder is genuinely not in trash anymore (restored or
// purged elsewhere) — distinct from transient network/server failures.
export class TrashFolderGoneError extends Error {}

export const trashQueryKey = ['trash'] as const

// Fetch trashed folders + assets
export function useTrash(enabled = true) {
	return useQuery({
		queryKey: trashQueryKey,
		queryFn: async () => {
			const response = await api.api.trash.get()
			if (response.error) throw new Error(handleApiError(response.error))
			return response.data as TrashResponse
		},
		enabled,
	})
}

// Fetch the contents (sub-folders + assets) of a trashed folder
export function useTrashFolder(folderId: string | null) {
	return useQuery({
		queryKey: [...trashQueryKey, 'folder', folderId],
		queryFn: async () => {
			const response = await api.api.trash.folder({ id: folderId as string }).get()
			if (response.error) {
				const message = handleApiError(response.error)
				if (response.status === 404 || response.status === 400) {
					throw new TrashFolderGoneError(message)
				}
				throw new Error(message)
			}
			return response.data as TrashFolderContents
		},
		enabled: !!folderId,
		// Don't waste retries on terminal "gone" responses
		retry: (failureCount, error) => !(error instanceof TrashFolderGoneError) && failureCount < 2,
	})
}

function useInvalidateTrashAndViews() {
	const queryClient = useQueryClient()
	return () => {
		queryClient.invalidateQueries({ queryKey: trashQueryKey })
		queryClient.invalidateQueries({ queryKey: queryKeys.assets.all })
		queryClient.invalidateQueries({ queryKey: queryKeys.fields.all })
		// Collections and browse counts/paths also filter BINNED server-side
		queryClient.invalidateQueries({ queryKey: queryKeys.collections.all })
		queryClient.invalidateQueries({ queryKey: ['browse'] })
		queryClient.invalidateQueries({ queryKey: queryKeys.activity.all })
	}
}

// Restore a trashed folder or asset. Returns an orphanWarning when confirmation
// is required (parent folder still trashed); re-call with confirmOrphan=true.
export function useRestore() {
	const invalidate = useInvalidateTrashAndViews()
	return useMutation({
		mutationFn: async (input: {
			type: 'folder' | 'asset'
			id: string
			confirmOrphan?: boolean
		}) => {
			const response = await api.api.trash.restore.post(input)
			if (response.error) throw new Error(handleApiError(response.error))
			return response.data as { success: boolean; orphanWarning?: OrphanWarning }
		},
		// Always refresh on settle so a stale/raced card doesn't linger after a failure.
		onSettled: () => invalidate(),
	})
}

// Permanently delete a trashed folder or asset (hard delete).
export function usePurge() {
	const invalidate = useInvalidateTrashAndViews()
	return useMutation({
		mutationFn: async (input: { type: 'folder' | 'asset'; id: string }) => {
			const response = await api.api.trash({ type: input.type })({ id: input.id }).delete()
			if (response.error) throw new Error(handleApiError(response.error))
			return response.data
		},
		onSettled: () => invalidate(),
	})
}
